import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
    CreateAgriculturalMaterialRequest,
    ListAgriculturalMaterialsParams,
    UpdateAgriculturalMaterialRequest,
} from '@/lib/api/agricultural-materials-api'
import { agriculturalMaterialsApi } from '@/lib/api/agricultural-materials-api'
import { formatApiError } from '@/lib/format-api-error'
import { m } from '@/lib/paraglide/messages'

export const agriMaterialKeys = {
    all: ['agricultural-materials'] as const,
    list: (params?: ListAgriculturalMaterialsParams) =>
        [...agriMaterialKeys.all, 'list', params ?? {}] as const,
    detail: (id: string) => [...agriMaterialKeys.all, 'detail', id] as const,
}

export function useAgriculturalMaterials(
    params?: ListAgriculturalMaterialsParams,
) {
    return useQuery({
        queryKey: agriMaterialKeys.list(params),
        queryFn: async () => {
            const res = await agriculturalMaterialsApi.list(params)
            return { items: res.data ?? [], meta: res.meta }
        },
    })
}

/** Infinite + search cho picker chọn vật tư (search API + load-more khi cuộn).
 *  Tuân rule "resource-select input" trong AGENTS.md. */
export function useAgriculturalMaterialsInfinite(
    params: Omit<ListAgriculturalMaterialsParams, 'page'> = {},
) {
    return useInfiniteQuery({
        queryKey: [...agriMaterialKeys.all, 'infinite', params] as const,
        queryFn: ({ pageParam }) =>
            agriculturalMaterialsApi.list({ ...params, page: pageParam }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const meta = lastPage.meta
            if (!meta) return undefined
            return meta.page < meta.total_pages ? meta.page + 1 : undefined
        },
    })
}

export function useAgriculturalMaterial(id?: string, enabled = true) {
    return useQuery({
        queryKey: agriMaterialKeys.detail(id ?? ''),
        enabled: Boolean(id) && enabled,
        queryFn: async () => {
            const res = await agriculturalMaterialsApi.get(id as string)
            return res.data ?? null
        },
    })
}

export function useCreateAgriculturalMaterial() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: CreateAgriculturalMaterialRequest) =>
            agriculturalMaterialsApi.create(data),
        onSuccess: () => {
            toast.success(m.agri_material_save_success())
            void qc.invalidateQueries({ queryKey: agriMaterialKeys.all })
        },
        onError: (err) => {
            const payload = formatApiError(err, m.agri_material_save_error())
            toast.error(payload.title, { description: payload.description })
        },
    })
}

export function useUpdateAgriculturalMaterial() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            id,
            data,
        }: {
            id: string
            data: UpdateAgriculturalMaterialRequest
        }) => agriculturalMaterialsApi.update(id, data),
        onSuccess: () => {
            toast.success(m.agri_material_save_success())
            void qc.invalidateQueries({ queryKey: agriMaterialKeys.all })
        },
        onError: (err) => {
            const payload = formatApiError(err, m.agri_material_save_error())
            toast.error(payload.title, { description: payload.description })
        },
    })
}

export function useDeleteAgriculturalMaterial() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => agriculturalMaterialsApi.remove(id),
        onSuccess: () => {
            toast.success(m.agri_material_delete_success())
            void qc.invalidateQueries({ queryKey: agriMaterialKeys.all })
        },
        onError: (err) => {
            const payload = formatApiError(err, m.agri_material_delete_error())
            toast.error(payload.title, { description: payload.description })
        },
    })
}
